import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Skeleton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  alpha,
  useTheme,
  TablePagination,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  People as PeopleIcon,
  Assignment as TrackerIcon,
  PersonAdd as PersonAddIcon,
  ManageAccounts as ManageAccountsIcon,
  CheckCircle,
  ArrowForward as ArrowForwardIcon,
  ChevronLeft,
  ChevronRight,
  WarningAmber as WarningIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Layout from '../Layout';
import api from '../api';
import { useAuth } from '../context/userContext';
import ComplianceOverview from '../Components/ComplianceOverview';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';
import {
  format,
  addMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
} from 'date-fns';
import { localDateKey } from '../utils/localDate';

const PageRoot = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : '#f5f6f8',
}));

const Panel = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  border: `1px solid ${theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.08) : '#e8eaed'}`,
  boxShadow: 'none',
  backgroundColor: theme.palette.background.paper,
}));

const StatCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  border: `1px solid ${theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.08) : '#e8eaed'}`,
  boxShadow: 'none',
  backgroundColor: theme.palette.background.paper,
  transition: 'border-color 0.2s, box-shadow 0.2s',
  '&:hover': {
    borderColor: alpha(theme.palette.primary.main, 0.3),
    boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.06)}`,
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '0.9375rem',
  color: theme.palette.text.secondary,
  letterSpacing: '0.02em',
  textTransform: 'uppercase',
  marginBottom: theme.spacing(1.5),
}));

const CalendarCard = styled(Panel)(({ theme }) => ({ padding: theme.spacing(2) }));
const DayBox = styled(Box)(({ theme }) => ({
  height: 44,
  borderRadius: 8,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  border: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(0.5),
}));
const Dot = styled('span')(({ color }) => ({
  display: 'inline-block',
  width: 6,
  height: 6,
  borderRadius: 999,
  background: color || '#9CA3AF',
}));
const DotRow = styled('div')(() => ({ display: 'flex', gap: 3, marginTop: 2, minHeight: 6 }));
const TrackerCard = styled(Card)(({ theme, color }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderLeft: `4px solid ${color || '#9CA3AF'}`,
  boxShadow: 'none',
  marginBottom: theme.spacing(1),
}));

const ROLE_COLORS = {
  admin: '#5e35b1',
  owner: '#7b1fa2',
  manager: '#e65100',
  seniormanager: '#1565c0',
  spocs: '#2e7d32',
  other: '#546e7a',
};

const formatRoleLabel = (role) => {
  const r = String(role || '').toLowerCase();
  if (r === 'seniormanager') return 'Senior Manager';
  if (r === 'spocs') return 'SPOC';
  return r.charAt(0).toUpperCase() + r.slice(1);
};

export default function AdminDashboard() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [{ user }] = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [calendarTrackers, setCalendarTrackers] = useState([]);
  const [monthOffset, setMonthOffset] = useState(0);
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [usersPage, setUsersPage] = useState(0);
  const [usersRowsPerPage, setUsersRowsPerPage] = useState(5);
  const today = useMemo(() => new Date(), []);
  const displayedMonth = useMemo(() => addMonths(today, monthOffset), [today, monthOffset]);
  const displayedFirstDay = useMemo(() => startOfMonth(displayedMonth), [displayedMonth]);
  const displayedLastDay = useMemo(() => endOfMonth(displayedMonth), [displayedMonth]);
  const startOfCal = useMemo(() => startOfWeek(displayedFirstDay, { weekStartsOn: 0 }), [displayedFirstDay]);
  const endOfCal = useMemo(() => endOfWeek(displayedLastDay, { weekStartsOn: 0 }), [displayedLastDay]);
  const calendarDays = useMemo(() => eachDayOfInterval({ start: startOfCal, end: endOfCal }), [startOfCal, endOfCal]);
  const weeks = useMemo(() => {
    const w = [];
    for (let i = 0; i < calendarDays.length; i += 7) w.push(calendarDays.slice(i, i + 7));
    return w;
  }, [calendarDays]);
  const trackersByDate = useMemo(() => {
    const map = {};
    calendarTrackers.forEach((t) => {
      const k = localDateKey(t.dueDate);
      if (!map[k]) map[k] = [];
      map[k].push(t);
    });
    return map;
  }, [calendarTrackers]);
  const getTrackersForDate = useMemo(() => (dateObj) => trackersByDate[localDateKey(dateObj)] || [], [trackersByDate]);
  const selectedDateTrackers = useMemo(() => getTrackersForDate(selectedDate), [getTrackersForDate, selectedDate]);
  const formatCalendarDay = (date) => format(date, 'd');
  const formatSelectedDateHeader = (date) => format(date, 'dd MMM yyyy');
  const calendarStatusColor = (s) => {
    const v = String(s || '').toLowerCase().trim();
    if (v === 'open') return theme.palette.warning.main;
    if (v === 'pending for review') return theme.palette.info.main;
    if (v === 'pending for discrepancy') return theme.palette.error.main;
    if (['complied', 'completed', 'not applicable'].includes(v)) return theme.palette.success.main;
    return '#9CA3AF';
  };
  const statusColor = (s) => {
    const v = String(s || '').toLowerCase().trim();
    if (v === 'open' || v === 'pending for discrepancy') return theme.palette.warning.main;
    if (v === 'pending for review') return theme.palette.secondary.main;
    if (['complied', 'completed', 'not applicable'].includes(v)) return theme.palette.success.main;
    return '#9CA3AF';
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const { data } = await api.get('detail/admin-dashboard-stats');
        setStats(data);
        setError(null);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load dashboard');
        setStats(null);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  useEffect(() => {
    if (!stats?.companyName || !user) return;
    const fetchTrackers = async () => {
      try {
        const { data: list } = await api.post('tracker/get-trackers', { company: stats.companyName, user });
        setCalendarTrackers(Array.isArray(list) ? list : []);
      } catch {
        setCalendarTrackers([]);
      }
    };
    fetchTrackers();
  }, [stats?.companyName, user]);

  const ts = stats?.trackersByStatus || {};
  const completedCount = (Number(ts.completed) || 0) + (Number(ts.complied) || 0);
  const notApplicableCount = Number(ts['not applicable']) || 0;
  const totalForRate = (stats?.totalTrackers ?? 0) - notApplicableCount;
  const complianceRate = totalForRate > 0 ? Math.round((completedCount / totalForRate) * 100) : 0;

  const last12Months = useMemo(() => {
    const list = [];
    const d = new Date();
    for (let i = 11; i >= 0; i--) {
      const m = new Date(d.getFullYear(), d.getMonth() - i, 1);
      list.push(format(m, 'yyyy-MM'));
    }
    return list;
  }, []);

  const usersAddedByMonthFilled = useMemo(() => {
    const map = {};
    (stats?.usersAddedByMonth || []).forEach(({ month, count }) => { map[month] = count; });
    return last12Months.map((month) => ({ month, count: map[month] || 0, label: format(new Date(month + '-01'), 'MMM yyyy') }));
  }, [stats?.usersAddedByMonth, last12Months]);

  const trackersCompletedByMonthFilled = useMemo(() => {
    const map = {};
    (stats?.trackersCompletedByMonth || []).forEach(({ month, count }) => { map[month] = count; });
    return last12Months.map((month) => ({ month, count: map[month] || 0, label: format(new Date(month + '-01'), 'MMM yyyy') }));
  }, [stats?.trackersCompletedByMonth, last12Months]);

  if (loading) {
    return (
      <Layout title="Admin Dashboard">
        <PageRoot>
          <Container sx={{ py: 4 }}>
            <Skeleton variant="text" width={280} height={40} sx={{ mb: 1 }} />
            <Skeleton variant="text" width={200} height={24} sx={{ mb: 4 }} />
            <Grid container spacing={3}>
              {[1, 2, 3, 4].map((i) => (
                <Grid item xs={12} sm={6} md={3} key={i}>
                  <Skeleton variant="rounded" height={120} sx={{ borderRadius: 2 }} />
                </Grid>
              ))}
            </Grid>
          </Container>
        </PageRoot>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout title="Admin Dashboard">
        <PageRoot>
          <Container sx={{ py: 4 }}>
            <Typography color="error" variant="body1">{error}</Typography>
          </Container>
        </PageRoot>
      </Layout>
    );
  }

  return (
    <Layout title="Admin Dashboard">
      <PageRoot>
        <Container sx={{ py: 4 }}>
          <Box sx={{ mb: 4, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>Admin Dashboard</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{stats?.companyName}</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => navigate('/add_user')} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}>Add user</Button>
              <Button variant="outlined" startIcon={<ManageAccountsIcon />} onClick={() => navigate('/manage/users')} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}>Manage users</Button>
            </Box>
          </Box>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard>
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>Total users</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>{stats?.totalUsers ?? 0}</Typography>
                    </Box>
                    <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.08), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <PeopleIcon sx={{ color: 'primary.main', fontSize: 24 }} />
                    </Box>
                  </Box>
                </CardContent>
              </StatCard>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard>
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>Total trackers</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>{stats?.totalTrackers ?? 0}</Typography>
                    </Box>
                    <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: alpha(theme.palette.info.main, 0.08), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <TrackerIcon sx={{ color: 'info.main', fontSize: 24 }} />
                    </Box>
                  </Box>
                </CardContent>
              </StatCard>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard>
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>Missed (overdue)</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5, color: stats?.missedCount > 0 ? 'error.main' : 'text.primary' }}>{stats?.missedCount ?? 0}</Typography>
                    </Box>
                    <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: alpha(theme.palette.error.main, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <WarningIcon sx={{ color: 'error.main', fontSize: 24 }} />
                    </Box>
                  </Box>
                </CardContent>
              </StatCard>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard>
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>Compliance rate</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5, color: complianceRate >= 80 ? 'success.main' : complianceRate >= 50 ? 'warning.main' : 'text.primary' }}>{complianceRate}%</Typography>
                    </Box>
                    <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: alpha(theme.palette.success.main, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <TrendingUpIcon sx={{ color: 'success.main', fontSize: 24 }} />
                    </Box>
                  </Box>
                </CardContent>
              </StatCard>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard sx={{ cursor: 'pointer' }} onClick={() => navigate('/add_user')}>
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>Quick action</Typography>
                    <ArrowForwardIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 600, mt: 1 }}>Add new user</Typography>
                  <Typography variant="caption" color="text.secondary">Invite someone to your organization</Typography>
                </CardContent>
              </StatCard>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard sx={{ cursor: 'pointer' }} onClick={() => navigate('/manage/users')}>
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>Quick action</Typography>
                    <ArrowForwardIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 600, mt: 1 }}>Manage users</Typography>
                  <Typography variant="caption" color="text.secondary">Edit roles, locations, and access</Typography>
                </CardContent>
              </StatCard>
            </Grid>
          </Grid>

          <ComplianceOverview />

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Panel>
                <CardContent sx={{ p: 3 }}>
                  <SectionTitle><PeopleIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} /> Users added (last 12 months)</SectionTitle>
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={usersAddedByMonthFilled} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
                      <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={0} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                      <Tooltip contentStyle={{ borderRadius: 8, border: `1px solid ${theme.palette.divider}` }} />
                      <Line type="monotone" dataKey="count" name="Users added" stroke={theme.palette.primary.main} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Panel>
            </Grid>
            <Grid item xs={12} md={6}>
              <Panel>
                <CardContent sx={{ p: 3 }}>
                  <SectionTitle><CheckCircle sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} /> Trackers completed (last 12 months)</SectionTitle>
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={trackersCompletedByMonthFilled} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
                      <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={0} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                      <Tooltip contentStyle={{ borderRadius: 8, border: `1px solid ${theme.palette.divider}` }} />
                      <Line type="monotone" dataKey="count" name="Completed" stroke={theme.palette.success.main} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Panel>
            </Grid>
            <Grid item xs={12}>
              <Panel>
                <CardContent sx={{ p: 3 }}>
                  <SectionTitle><PeopleIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} /> Recently added users</SectionTitle>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Edit user details or manage access from the list below.</Typography>
                  <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
                    <Table size="medium">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.04) : 'grey.50' }}>
                          <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Name</TableCell>
                          <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Email</TableCell>
                          <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Role</TableCell>
                          <TableCell sx={{ fontWeight: 600, py: 1.5 }}>Added</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, py: 1.5 }}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(stats?.recentUsers || []).length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                              <Typography variant="body2" color="text.secondary">No users yet. Use &quot;Add user&quot; to create the first.</Typography>
                              <Button size="small" sx={{ mt: 1.5 }} onClick={() => navigate('/add_user')}>Add user</Button>
                            </TableCell>
                          </TableRow>
                        ) : (
                          (stats?.recentUsers || []).slice(usersPage * usersRowsPerPage, usersPage * usersRowsPerPage + usersRowsPerPage).map((u) => (
                            <TableRow key={u._id} hover>
                              <TableCell sx={{ py: 1.5 }}>{u.name}</TableCell>
                              <TableCell sx={{ py: 1.5 }}>{u.email}</TableCell>
                              <TableCell sx={{ py: 1.5 }}>
                                <Chip size="small" label={formatRoleLabel(u.role)} sx={{ fontWeight: 500, bgcolor: alpha(ROLE_COLORS[u.role?.toLowerCase()] || ROLE_COLORS.other, 0.12), color: ROLE_COLORS[u.role?.toLowerCase()] || ROLE_COLORS.other, border: 'none' }} />
                              </TableCell>
                              <TableCell sx={{ py: 1.5 }}>{u.createdAt ? format(new Date(u.createdAt), 'd MMM yyyy') : '—'}</TableCell>
                              <TableCell align="right" sx={{ py: 1.5 }}>
                                <Button size="small" variant="text" onClick={() => navigate(`/edit-user/${u._id}`)} sx={{ textTransform: 'none', fontWeight: 600 }}>Edit</Button>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {(stats?.recentUsers || []).length > 0 && (
                    <>
                      <TablePagination component="div" count={(stats?.recentUsers || []).length} page={usersPage} onPageChange={(_, p) => setUsersPage(p)} rowsPerPage={usersRowsPerPage} onRowsPerPageChange={(e) => { setUsersRowsPerPage(parseInt(e.target.value, 10)); setUsersPage(0); }} rowsPerPageOptions={[5, 10, 25]} labelRowsPerPage="Rows:" sx={{ borderTop: `1px solid ${theme.palette.divider}` }} />
                      <Box sx={{ mt: 2 }}>
                        <Button size="small" endIcon={<ArrowForwardIcon />} onClick={() => navigate('/manage/users')} sx={{ textTransform: 'none', fontWeight: 600 }}>View all users</Button>
                      </Box>
                    </>
                  )}
                </CardContent>
              </Panel>
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mt: 3, mb: 3 }}>
            <Grid item xs={12} md={4}>
              <CalendarCard>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>Tracker calendar</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Button size="small" variant="outlined" onClick={() => setMonthOffset((p) => p - 1)} startIcon={<ChevronLeft />} sx={{ textTransform: 'none' }}>Prev</Button>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{format(displayedMonth, 'MMMM yyyy')}</Typography>
                  <Button size="small" variant="outlined" onClick={() => setMonthOffset((p) => p + 1)} endIcon={<ChevronRight />} sx={{ textTransform: 'none' }}>Next</Button>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: 0.5, px: 0.5 }}>
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((w) => (
                    <Typography key={w} variant="caption" align="center" sx={{ fontWeight: 600, color: 'text.secondary' }}>{w}</Typography>
                  ))}
                </Box>
                {weeks.map((week, wi) => (
                  <Box key={wi} sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 0.5 }}>
                    {week.map((dateObj) => {
                      const trackers = getTrackersForDate(dateObj);
                      const inMonth = isSameMonth(dateObj, displayedMonth);
                      const distinct = Array.from(new Set((trackers || []).map((t) => String(t?.status || '').toLowerCase().trim()).filter(Boolean)));
                      const dots = distinct.slice(0, 3);
                      const overflow = distinct.length - 3;
                      return (
                        <DayBox key={dateObj.toString()} onClick={() => setSelectedDate(dateObj)} sx={{ opacity: inMonth ? 1 : 0.45, cursor: 'pointer' }}>
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>{formatCalendarDay(dateObj)}</Typography>
                          {dots.length > 0 && (
                            <DotRow>
                              {dots.map((s) => <Dot key={s} color={calendarStatusColor(s)} />)}
                              {overflow > 0 && <Typography variant="caption" sx={{ fontSize: 9 }}>+{overflow}</Typography>}
                            </DotRow>
                          )}
                        </DayBox>
                      );
                    })}
                  </Box>
                ))}
              </CalendarCard>
            </Grid>
            <Grid item xs={12} md={8}>
              <Panel sx={{ p: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>Trackers for {formatSelectedDateHeader(selectedDate)}</Typography>
                {selectedDateTrackers.length > 0 ? (
                  selectedDateTrackers.slice(0, 8).map((tracker) => (
                    <TrackerCard key={tracker.uniqueIdentifier} color={statusColor(tracker.status)}>
                      <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => navigate(`/Edit/${tracker.uniqueIdentifier}`)}>{tracker.uniqueIdentifier}</Typography>
                        <Typography variant="caption" display="block">{tracker.complianceActivity}</Typography>
                        <Typography variant="caption" color="text.secondary">{tracker.status}</Typography>
                      </CardContent>
                    </TrackerCard>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">No trackers for this date.</Typography>
                )}
              </Panel>
            </Grid>
          </Grid>
        </Container>
      </PageRoot>
    </Layout>
  );
}
